import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import type { UserFragment } from '@/graphql/saleor/api';
import { useUser } from '@/hooks/useUser';
import { EMAIL_REGX } from '@/lib/forms/const';
import { formMessages } from '@/messages/forms';
import * as M from '@/messages/newsletter';
import { useRegionProvider } from '@/providers/RegionProvider';

import type { NewsLetterSchema } from './schema';

import * as S from './NewsLetterView.styles';

export interface NewsLetterFormProps {
  handleSubmit: SubmitHandler<NewsLetterSchema>;
  isProcessing?: boolean;
}

export type NewsLetterUnsubscribeFormSchema = InferType<
  ReturnType<typeof newsLetterUnsubscribeFormSchema>
>;

export const newsLetterUnsubscribeFormSchema = (
  formatMessage: IntlShape['formatMessage'],
  user: Partial<UserFragment> | null,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  return object({
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(user?.email ?? ''),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};

export const NewsLetterUnsubscribeForm = ({
  handleSubmit,
  isProcessing = false,
}: NewsLetterFormProps) => {
  const [isVerifyingRecaptchaStatus, setIsVerifyingRecaptchaStatus] =
    useState(false);
  const [isFormFieldDisabled, setIsFormFieldDisabled] = useState(false);
  const {
    query: { languageCode },
  } = useRegionProvider();

  const {
    snapshot: { user },
  } = useUser();
  useEffect(() => {
    if (user?.id) {
      setIsFormFieldDisabled(true);
    }
  }, [user?.id]);
  const { formatMessage } = useIntl();

  const schema = newsLetterUnsubscribeFormSchema(formatMessage, user);

  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });

  const captcha = formCtx.watch('captcha');

  return (
    <FormProvider
      name='sign-up-my-account'
      {...formCtx}
      background='#FAFAFA'
      onSubmit={handleSubmit}
    >
      <S.Row $widthLimit={true}>
        <Col>
          <InputField
            autoComplete='email'
            colorVariant='white'
            data-testid='email'
            disabled={isFormFieldDisabled}
            id='inputEmail'
            name='email'
            placeholder={formatMessage(formMessages.email)}
            required
            style={{ border: '1px solid hsla(0,0%,39%,.3)' }}
            type='email'
          />
        </Col>
      </S.Row>
      <S.Row>
        <Col>
          <ReCaptcha
            languageCode={languageCode}
            name='captcha'
            onChange={setIsVerifyingRecaptchaStatus}
          />
        </Col>
      </S.Row>
      <S.Row>
        <Col>
          <Flex justifyContent='center'>
            <LegacyButton
              data-testid='unsubscribeSubmitButton'
              disabled={isProcessing || !captcha || isVerifyingRecaptchaStatus}
              isProcessing={isProcessing || isVerifyingRecaptchaStatus}
              label={formatMessage(M.defined.newsLetterButton)}
              type='submit'
            />
          </Flex>
        </Col>
      </S.Row>
    </FormProvider>
  );
};
